import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getPatientDiagnosesService,
  searchDiagnosesService,
} from "../api/services";
import { useRequestError } from "../hooks/useRequestError";
import type { DiagnosisSummaryParams } from "../types/params";
import {
  hasMeaningfulDiagnosisQuery,
  mapBackendDiagnosesToSummary,
  mapDiagnosisSearchResponse,
  mergeDiagnosesWithMutations,
  type DiagnosisMutationsState,
} from "../utils/diagnosisFlow";

export const DiagnosisFinderPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { handleRequestError } = useRequestError();
  const userId = params.get("user_id") || "";
  const planId = params.get("plan_id") || "";

  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState<DiagnosisSummaryParams[]>([]);
  const [initialDiagnoses, setInitialDiagnoses] = useState<
    DiagnosisSummaryParams[]
  >([]);
  const [selected, setSelected] = useState<DiagnosisSummaryParams[]>([]);
  const [mutations, setMutations] = useState<DiagnosisMutationsState>({
    add: [],
    remove_ids: [],
    removed_existing_codes: [],
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialFailed, setInitialFailed] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);

  const hasMeaningfulQuery = hasMeaningfulDiagnosisQuery(debounced);

  useEffect(() => {
    let routeSelected: DiagnosisSummaryParams[] = [];
    let routeMutations: DiagnosisMutationsState = {
      add: [],
      remove_ids: [],
      removed_existing_codes: [],
    };
    try {
      const rawSelected = sessionStorage.getItem("reeva.plan.selectedDiagnoses");
      const rawMutations = sessionStorage.getItem(
        "reeva.plan.diagnosisMutations"
      );
      if (rawSelected) routeSelected = JSON.parse(rawSelected);
      if (rawMutations) routeMutations = JSON.parse(rawMutations);
    } catch {
      // ignore
    }
    setMutations(routeMutations);

    const hydrate = async () => {
      if (!userId) {
        setInitialLoading(false);
        setSelected(routeSelected);
        return;
      }
      setInitialLoading(true);
      setInitialFailed(false);
      try {
        const response = await getPatientDiagnosesService(userId);
        const backendDiagnoses = mapBackendDiagnosesToSummary(response || []);
        setInitialDiagnoses(backendDiagnoses);
        setSelected(
          mergeDiagnosesWithMutations({
            backendDiagnoses,
            routeSelectedDiagnoses: routeSelected,
            mutations: routeMutations,
          })
        );
      } catch (error) {
        setInitialFailed(true);
        setSelected(routeSelected);
        handleRequestError(error);
      } finally {
        setInitialLoading(false);
      }
    };

    hydrate();
  }, [userId, handleRequestError]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 350);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const run = async () => {
      if (!hasMeaningfulDiagnosisQuery(debounced)) {
        setResults([]);
        setSearchFailed(false);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      setSearchFailed(false);
      try {
        const response = await searchDiagnosesService({
          q: debounced,
          limit: 20,
          offset: 0,
        });
        setResults(mapDiagnosisSearchResponse(response));
      } catch (error) {
        setSearchFailed(true);
        setResults([]);
        handleRequestError(error);
      } finally {
        setSearchLoading(false);
      }
    };
    run();
  }, [debounced, handleRequestError]);

  const selectedCodes = useMemo(
    () => new Set(selected.map((item) => item.icd_code)),
    [selected]
  );

  const findInitial = (icdCode: string) =>
    initialDiagnoses.find((item) => item.icd_code === icdCode);

  const addDiagnosis = (item: DiagnosisSummaryParams) => {
    if (selectedCodes.has(item.icd_code)) return;
    setSelected((prev) => [...prev, item]);
    const initial = findInitial(item.icd_code);
    setMutations((prev) => {
      if (initial) {
        return {
          ...prev,
          remove_ids: initial.id
            ? prev.remove_ids.filter((id) => id !== initial.id)
            : prev.remove_ids,
          removed_existing_codes: prev.removed_existing_codes.filter(
            (code) => code !== item.icd_code
          ),
        };
      }
      if (prev.add.some((x) => x.icd_code === item.icd_code)) return prev;
      return { ...prev, add: [...prev.add, item] };
    });
  };

  const removeDiagnosis = (icdCode: string) => {
    setSelected((prev) => prev.filter((x) => x.icd_code !== icdCode));
    const initial = findInitial(icdCode);
    setMutations((prev) => {
      if (initial) {
        return {
          ...prev,
          remove_ids:
            initial.id && !prev.remove_ids.includes(initial.id)
              ? [...prev.remove_ids, initial.id]
              : prev.remove_ids,
          removed_existing_codes: prev.removed_existing_codes.includes(icdCode)
            ? prev.removed_existing_codes
            : [...prev.removed_existing_codes, icdCode],
        };
      }
      return {
        ...prev,
        add: prev.add.filter((x) => x.icd_code !== icdCode),
      };
    });
  };

  const done = () => {
    sessionStorage.setItem(
      "reeva.plan.selectedDiagnoses",
      JSON.stringify(selected)
    );
    sessionStorage.setItem(
      "reeva.plan.diagnosisMutations",
      JSON.stringify(mutations)
    );
    const q = new URLSearchParams({ user_id: userId });
    if (planId) q.set("plan_id", planId);
    navigate(`/plans/setup?${q.toString()}`);
  };

  const searchHint = (() => {
    if (!query.trim()) {
      return "Type at least 2 characters to search.";
    }
    if (!hasMeaningfulQuery) {
      return "Keep typing to search ICD-11 diagnoses.";
    }
    return null;
  })();

  return (
    <div className="page diagnosis-page">
      <header className="page-header">
        <div>
          <h1>Diagnosis Finder</h1>
          <p className="muted">Search ICD-11 diagnosis by code or name.</p>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </header>

      <div className="diagnosis-search">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search ICD-11 codes or names"
          aria-label="Search ICD-11 codes or names"
        />
      </div>

      <button type="button" className="btn btn-primary diagnosis-done" onClick={done}>
        Done ({selected.length})
      </button>

      <section className="diagnosis-selected">
        <div className="row-between">
          <h3>Selected Diagnoses</h3>
          <span className="pill">{selected.length} Added</span>
        </div>
        {initialLoading ? (
          <div className="diagnosis-state">Loading patient diagnoses…</div>
        ) : initialFailed ? (
          <div className="diagnosis-state">Could not load current diagnoses.</div>
        ) : selected.length === 0 ? (
          <div className="diagnosis-state">No diagnoses selected yet.</div>
        ) : (
          <ul className="diagnosis-selected-list">
            {selected.map((item) => (
              <li key={item.icd_code} className="diagnosis-selected-item">
                <div className="diagnosis-selected-text">
                  <strong className="diagnosis-selected-title">
                    {item.title}
                  </strong>
                  <span className="diagnosis-selected-code">{item.icd_code}</span>
                </div>
                <button
                  type="button"
                  className="diagnosis-selected-remove"
                  onClick={() => removeDiagnosis(item.icd_code)}
                  aria-label={`Remove ${item.title}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="diagnosis-results-wrap">
        <h3>Search Results</h3>
        <div className="diagnosis-results-panel">
          {searchHint ? (
            <div className="diagnosis-state">{searchHint}</div>
          ) : searchLoading ? (
            <div className="diagnosis-state">
              <div className="spinner" />
              Searching diagnoses…
            </div>
          ) : searchFailed ? (
            <div className="diagnosis-state">Search failed. Please try again.</div>
          ) : results.length > 0 ? (
            <div className="diagnosis-results-list">
              {results.map((item) => {
                const selectedItem = selectedCodes.has(item.icd_code);
                return (
                  <button
                    key={item.icd_code}
                    type="button"
                    className={`diagnosis-result-row${
                      selectedItem ? " selected" : ""
                    }`}
                    onClick={() =>
                      selectedItem
                        ? removeDiagnosis(item.icd_code)
                        : addDiagnosis(item)
                    }
                  >
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.icd_code}</span>
                    </div>
                    <span className="diagnosis-result-action">
                      {selectedItem ? "−" : "+"}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="diagnosis-state empty-results">
              <div className="diagnosis-empty-icon" aria-hidden>
                ⌕
              </div>
              <p>Can&apos;t find your diagnosis? Try searching by symptoms.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
